import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AppsRounded,
  BusinessRounded,
  ChevronRightRounded,
  ManageAccountsRounded,
  ShieldOutlined,
  TuneRounded,
} from "@mui/icons-material";
import { Chip, MenuItem } from "@mui/material";
import { Identicon, SharingReferences, ArtBox } from "@art/shared-ui";
import { useSelector } from "react-redux";
import type { RootState } from "../../store";
import { useAttributePermission } from "../../global/permissions";
import type { SettingsCategory, SettingsCategoryKey } from "./settings-menu";
import { getCategoryForPath } from "./settings-menu";
import styles from "./header.module.scss";

const PROFILE_IMAGE_FIELDS = [
  "avatar_url",
  "profile_image_url",
  "profile_image",
  "picture",
] as const;

const categoryIcons: Record<SettingsCategoryKey, React.ReactNode> = {
  app: <AppsRounded fontSize="small" />,
  platform: <TuneRounded fontSize="small" />,
  personal: <ManageAccountsRounded fontSize="small" />,
};

function firstNonEmptyString(...values: unknown[]): string {
  return values.find(
    (value): value is string =>
      typeof value === "string" && value.trim().length > 0,
  )?.trim() ?? "";
}

function profileImage(user: any): string {
  return firstNonEmptyString(
    ...PROFILE_IMAGE_FIELDS.map((field) => user?.[field]),
  );
}

function profileName(user: any): string {
  const composedName = [user?.first_name, user?.last_name]
    .filter((part) => typeof part === "string" && part.trim())
    .join(" ")
    .trim();

  return firstNonEmptyString(
    composedName,
    user?.display_name,
    user?.name,
    typeof user?.email === "string" ? user.email.split("@")[0] : "",
    "ART user",
  );
}

function normalizedRoles(user: any): string[] {
  const source = user?.tenant?.roles ?? user?.roles ?? [];
  if (!Array.isArray(source)) return [];

  return Array.from(
    new Set(
      source
        .map((role: any) =>
          typeof role === "string"
            ? role.trim()
            : firstNonEmptyString(role?.name, role?.title),
        )
        .filter(Boolean),
    ),
  );
}

interface ProfileAvatarProps {
  user: any;
  size: number;
  className?: string;
}

export function ProfileAvatar({ user, size, className }: ProfileAvatarProps) {
  const imageUrl = profileImage(user);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => setImageFailed(false), [imageUrl]);

  if (imageUrl && !imageFailed) {
    return (
      <img
        src={imageUrl}
        alt=""
        className={className}
        width={size}
        height={size}
        onError={() => setImageFailed(true)}
      />
    );
  }

  return (
    <span className={className} aria-hidden="true">
      <Identicon
        size={size}
        value={firstNonEmptyString(user?.email, profileName(user), "ART user")}
        config={{ backColor: "#ede6e6", padding: 0.12 }}
        style={{ borderRadius: size }}
      />
    </span>
  );
}

interface ProfileMenuContentProps {
  user: any;
  tenantName?: string | null;
  categories: SettingsCategory[];
  pathname: string;
  onNavigate: () => void;
}

export default function ProfileMenuContent({
  user,
  tenantName,
  categories,
  pathname,
  onNavigate,
}: ProfileMenuContentProps) {
  const appState = useSelector((state: RootState) => state.appstate);
  const canCopyReferences = useAttributePermission("ACCOUNT_SETTINGS", "PROFILE_MANAGEMENT", "LoadProfile");
  const tenant = user?.tenant;
  const name = profileName(user);
  const email = firstNonEmptyString(user?.email, "Email unavailable");
  const resolvedTenantName = firstNonEmptyString(
    tenant?.tenant_name,
    tenant?.name,
    tenantName,
    "Tenant unavailable",
  );
  const roles = useMemo(() => normalizedRoles(user), [user]);
  const visibleRoles = roles.slice(0, 2);
  const ownership = tenant?.is_owner;
  const activeCategory = getCategoryForPath(pathname).key;
  const isSettingsPage =
    pathname.startsWith("/application-settings") ||
    pathname.startsWith("/account-settings") ||
    pathname.startsWith("/platform-settings");

  return (
    <div className={styles.profile_menu} data-qa="profile-menu">
      <section className={styles.profile_identity} aria-label="Signed-in profile">
        <div className={styles.profile_identity_avatar}>
          <ProfileAvatar
            user={user}
            size={44}
            className={styles.profile_avatar_image}
          />
        </div>
        <div className={styles.profile_identity_copy}>
          <strong title={name}>{name}</strong>
          <span title={email}>{email}</span>
          <Chip
            size="small"
            variant="outlined"
            label={
              ownership === true
                ? "Tenant owner"
                : ownership === false
                  ? "Tenant member"
                  : "Ownership unavailable"
            }
            className={styles.profile_ownership_badge}
          />
        </div>
      </section>

      <section className={styles.profile_access} aria-label="Tenant access">
        <div className={styles.profile_access_row}>
          <BusinessRounded aria-hidden="true" />
          <div>
            <span>Tenant</span>
            <strong title={resolvedTenantName}>{resolvedTenantName}</strong>
          </div>
        </div>

        <div className={styles.profile_access_row}>
          <ShieldOutlined aria-hidden="true" />
          <div>
            <span>{roles.length === 1 ? "Role" : "Roles"}</span>
            <div
              className={styles.profile_role_list}
              title={roles.length > 0 ? roles.join(", ") : "No assigned role"}
            >
              {visibleRoles.length > 0 ? (
                <>
                  {visibleRoles.map((role) => (
                    <Chip
                      key={role}
                      size="small"
                      label={role}
                      className={styles.profile_role_badge}
                    />
                  ))}
                  {roles.length > visibleRoles.length && (
                    <Chip
                      size="small"
                      label={`+${roles.length - visibleRoles.length}`}
                      className={styles.profile_role_count}
                    />
                  )}
                </>
              ) : (
                <strong>No assigned role</strong>
              )}
            </div>
          </div>
        </div>
      </section>

      {canCopyReferences && <ArtBox sx={{px: 2, py: 1, borderTop: 1, borderColor: 'divider'}}>
        <SharingReferences
          basePath="agent-configuration/agent"
          scopeKey={`${tenantName}:${appState.project_key}:${appState.environment}:${user?.email}`}
          scopeLabel={[resolvedTenantName, appState.project_name, appState.environment].filter(Boolean).join(' / ')}
          enabled={!!tenantName && !!appState.project_key && !!appState.environment}
        />
      </ArtBox>}

      <nav className={styles.profile_navigation} aria-label="Account navigation">
        <span className={styles.profile_navigation_label}>Settings</span>
        {categories.map((category, index) => {
          const isActive = isSettingsPage && activeCategory === category.key;

          return (
            <MenuItem
              key={category.key}
              selected={isActive}
              component={Link}
              to={category.defaultHref}
              onClick={onNavigate}
              onFocus={
                index === 0
                  ? (event) => {
                      const menu = event.currentTarget.closest('[role="menu"]');
                      requestAnimationFrame(() => {
                        if (menu) menu.scrollTop = 0;
                      });
                    }
                  : undefined
              }
              data-qa={`drop-menu-settings-${category.key}`}
              className={styles.profile_navigation_item}
            >
              <span className={styles.profile_navigation_icon} aria-hidden="true">
                {categoryIcons[category.key]}
              </span>
              <span>{category.label}</span>
              <ChevronRightRounded
                className={styles.profile_navigation_chevron}
                aria-hidden="true"
              />
            </MenuItem>
          );
        })}
      </nav>
    </div>
  );
}
